import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { Btn, InputField, Field, AIBadge } from './UIComponents';
import api from '../../services/api';

export function CarePlanModal({ onClose, plan, isEdit, aiMode, onSave }) {
  const { B } = useTheme();
  const [residents, setResidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    resident_id: plan?.resident_id || '',
    plan_type: plan?.plan_type || '',
    title: plan?.title || '',
    description: plan?.description || '',
    goals: plan?.goals || '',
    interventions: plan?.interventions || '',
    review_date: plan?.review_date || ''
  });

  useEffect(()=> {
    const fetchResidents = async () => {
      try {
        const res = await api.get('/residents', { params: { limit: 100, status: 'active' } });
        setResidents(res.data.data);
      } catch (err) {
        console.error('Failed to fetch residents:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchResidents();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    if (!formData.resident_id || !formData.plan_type || !formData.title || !formData.review_date) {
      alert('Please fill in all required fields.');
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit && plan?.id) {
        await api.put(`/care-plans/${plan.id}`, formData);
      } else {
        await api.post('/care-plans', formData);
      }
      if (onSave) onSave();
      onClose();
    } catch (err) {
      console.error('Failed to save care plan:', err);
      alert(err.response?.data?.message || 'Failed to save care plan');
    } finally {
      setSubmitting(false);
    }
  };

  const planTypes = [
    { value: 'bsp', label: 'BSP (Behavioral Support Plan)' },
    { value: 'isp', label: 'ISP (Individual Service Plan)' },
    { value: 'cip', label: 'CIP (Care Integration Plan)' },
    { value: 'nursing', label: 'Nursing Care Plan' },
    { value: 'therapy', label: 'Therapy Plan' }
  ];

  const modalStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  };

  const modalContentStyle = {
    background: B.cardBg,
    borderRadius: 16,
    width: '100%',
    maxWidth: 550,
    padding: 28,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    '@media (maxWidth: 768px)': {
      width: '90%',
      padding: 16,
    },
  };

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 8,
    border: `1.5px solid ${B.border}`,
    fontSize: 13,
    background: B.inputBg,
    color: B.text,
    outline: 'none',
    fontFamily: 'inherit',
  };

  const buttonContainerStyle = {
    display: 'flex',
    gap: 8,
    justifyContent: 'flex-end',
    '@media (maxWidth: 768px)': {
      flexDirection: 'column',
      gap: 8,
    },
  };

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ fontSize: 18, fontWeight: 800, color: B.navy }}>
            {isEdit ? 'Edit Care Plan' : 'New Care Plan'}
          </div>
          {aiMode && <AIBadge label="AI-Assisted" />}
        </div>

        {/* Resident Selection */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: B.text, display: 'block', marginBottom: 4 }}>
            Resident <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <select
            name="resident_id"
            value={formData.resident_id}
            onChange={handleChange}
            style={inputStyle}
            disabled={loading}
          >
            <option value="">
              {loading ? 'Loading residents...' : 'Select a resident...'}
            </option>
            {residents.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} (Room {r.room_number})
              </option>
            ))}
          </select>
        </div>

        {/* Plan Type */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: B.text, display: 'block', marginBottom: 4 }}>
            Plan Type <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <select
            name="plan_type"
            value={formData.plan_type}
            onChange={handleChange}
            style={inputStyle}
          >
            <option value="">Select plan type...</option>
            {planTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>

        {/* Plan Title */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: B.text, display: 'block', marginBottom: 4 }}>
            Plan Title <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            placeholder="e.g., Diabetes Management Plan"
            style={inputStyle}
          />
        </div>

        {/* Description */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: B.text, display: 'block', marginBottom: 4 }}>
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Brief description of the care plan..."
            rows="2"
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        {/* Goals */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: B.text, display: 'block', marginBottom: 4 }}>
            Goals
          </label>
          <textarea
            name="goals"
            value={formData.goals}
            onChange={handleChange}
            placeholder="List the care goals..."
            rows="3"
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        {/* Interventions */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: B.text, display: 'block', marginBottom: 4 }}>
            Interventions
          </label>
          <textarea
            name="interventions"
            value={formData.interventions}
            onChange={handleChange}
            placeholder="Planned interventions and strategies..."
            rows="3"
            style={{ ...inputStyle, resize: 'vertical' }}
          />
        </div>

        {/* Review Date */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, color: B.text, display: 'block', marginBottom: 4 }}>
            Review Date <span style={{ color: '#dc2626' }}>*</span>
          </label>
          <input
            type="date"
            name="review_date"
            value={formData.review_date}
            onChange={handleChange}
            style={inputStyle}
          />
        </div>

        <div style={buttonContainerStyle}>
          <Btn outline color={B.muted} onClick={onClose}>
            Cancel
          </Btn>
          <Btn color={B.teal} onClick={handleSubmit} disabled={submitting}>
            {submitting ? 'Saving...' : isEdit ? 'Update Care Plan' : 'Save Care Plan'}
          </Btn>
        </div>
      </div>
    </div>
  );
}

export function ViewCarePlanModal({ plan, onClose, aiMode }) {
  const { B } = useTheme();
  if (!plan) return null;

  const planTypeLabels = {
    bsp: 'BSP (Behavioral Support Plan)',
    isp: 'ISP (Individual Service Plan)',
    cip: 'CIP (Care Integration Plan)',
    nursing: 'Nursing Care Plan',
    therapy: 'Therapy Plan'
  };

  const modalStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    zIndex: 1000,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  };

  const modalContentStyle = {
    background: B.cardBg,
    borderRadius: 16,
    width: '100%',
    maxWidth: 500,
    padding: 28,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
    '@media (maxWidth: 768px)': {
      width: '90%',
      padding: 16,
    },
  };

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
        <div style={{ fontSize: 18, fontWeight: 800, color: B.navy, marginBottom: 20 }}>
          Care Plan Details
        </div>
        <Field label="Resident" value={plan.resident_name || plan.resident} />
        <Field label="Type" value={planTypeLabels[plan.plan_type] || plan.plan_type} />
        <Field label="Title" value={plan.title} />
        {plan.description && <Field label="Description" value={plan.description} />}
        <Field label="Goals" value={plan.goals || 'Not specified'} ai={aiMode} />
        <Field label="Interventions" value={plan.interventions || 'Not specified'} ai={aiMode} />
        <Field label="Review Date" value={new Date(plan.review_date).toLocaleDateString()} />
        <Field label="Status" value={plan.status} />
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <Btn outline color={B.muted} onClick={onClose}>
            Close
          </Btn>
        </div>
      </div>
    </div>
  );
}

export default CarePlanModal;